/**
 * Termos de Serviço
 * Página pública com layout consistente do sistema
 */

import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, FileText } from "lucide-react";

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen gradient-warm">
      {/* Header fixo com navegação */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/auth?mode=login')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para login
          </Button>
        </div>
      </header>

      {/* Conteúdo principal */}
      <main className="container max-w-4xl py-8 space-y-8">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <FileText className="h-8 w-8 text-primary" />
              <CardTitle className="text-3xl">Termos de Serviço</CardTitle>
            </div>
            <CardDescription>
              Última atualização: 30 de outubro de 2025
            </CardDescription>
          </CardHeader>

          <Separator />

          <CardContent className="pt-6 space-y-8">
            {/* 1. Aceitação dos Termos */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">1.</span>
                Aceitação dos Termos
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  Ao criar uma conta e utilizar o Pudinzim Sorocaba, você concorda em cumprir e estar vinculado aos presentes Termos de Serviço. Se você não concorda com qualquer parte destes termos, não utilize nosso sistema.
                </p>
                <p>
                  Estes termos constituem um acordo legal entre você (pessoa física ou jurídica) e o Pudinzim Sorocaba, estabelecendo as condições de uso da plataforma.
                </p>
              </div>
            </div>

            {/* 2. Descrição do Serviço */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">2.</span>
                Descrição do Serviço
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  O Pudinzim Sorocaba é um sistema de gestão empresarial desenvolvido especificamente para confeitarias e estabelecimentos similares. Nosso serviço oferece:
                </p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Controle de estoque de produtos e ingredientes</li>
                  <li>Gerenciamento de receitas e fichas técnicas</li>
                  <li>Registro e controle de vendas</li>
                  <li>Cadastro de clientes e fornecedores</li>
                  <li>Gestão de entrada de mercadorias</li>
                  <li>Controle de múltiplos estabelecimentos</li>
                  <li>Relatórios e análises de desempenho</li>
                </ul>
                <p>
                  O serviço é fornecido através de uma plataforma web acessível por navegadores modernos e oferece diferentes planos de assinatura para atender às necessidades de cada negócio.
                </p>
              </div>
            </div>

            {/* 3. Cadastro e Responsabilidades */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">3.</span>
                Cadastro e Responsabilidades do Usuário
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  Para utilizar o Pudinzim Sorocaba, você deve:
                </p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Fornecer informações verdadeiras, precisas e completas durante o cadastro</li>
                  <li>Manter suas credenciais de acesso (e-mail e senha) em sigilo absoluto</li>
                  <li>Notificar imediatamente qualquer uso não autorizado de sua conta</li>
                  <li>Ser responsável por todas as atividades realizadas em sua conta</li>
                  <li>Ter capacidade legal para celebrar este contrato</li>
                </ul>
                <p>
                  Você é o único responsável pela segurança de sua senha e por todas as ações realizadas através de sua conta. O Pudinzim Sorocaba não se responsabiliza por perdas ou danos decorrentes do uso não autorizado de suas credenciais.
                </p>
              </div>
            </div>

            {/* 4. Dados e Privacidade */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">4.</span>
                Dados e Privacidade
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  O tratamento de seus dados pessoais é regido pela nossa Política de Privacidade, que está em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
                </p>
                <p>
                  Você mantém a propriedade completa de todos os dados que inserir no sistema, incluindo:
                </p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Informações de clientes e fornecedores</li>
                  <li>Dados de produtos, receitas e estoque</li>
                  <li>Registros de vendas e movimentações financeiras</li>
                  <li>Quaisquer outros dados cadastrados na plataforma</li>
                </ul>
                <p>
                  O Pudinzim Sorocaba utiliza seus dados exclusivamente para fornecer e melhorar o serviço, nunca compartilhando suas informações comerciais com terceiros sem seu consentimento expresso.
                </p>
              </div>
            </div>

            {/* 5. Propriedade Intelectual */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">5.</span>
                Propriedade Intelectual
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  Todo o conteúdo do Pudinzim Sorocaba, incluindo mas não limitado a design, código-fonte, logotipos, textos, gráficos e funcionalidades, é propriedade exclusiva do Pudinzim Sorocaba e está protegido por leis de propriedade intelectual.
                </p>
                <p>
                  É expressamente proibido:
                </p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Copiar, modificar ou distribuir qualquer parte do sistema</li>
                  <li>Realizar engenharia reversa do código ou funcionalidades</li>
                  <li>Criar obras derivadas baseadas no sistema</li>
                  <li>Usar o sistema para desenvolver produtos concorrentes</li>
                  <li>Remover avisos de copyright ou marcas registradas</li>
                </ul>
              </div>
            </div>

            {/* 6. Limitação de Responsabilidade */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">6.</span>
                Limitação de Responsabilidade
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  O Pudinzim Sorocaba é fornecido "como está" e "conforme disponível". Embora nos esforcemos para manter o serviço sempre operacional e livre de erros, não garantimos:
                </p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Funcionamento ininterrupto ou livre de erros</li>
                  <li>Ausência total de vulnerabilidades de segurança</li>
                  <li>Adequação a todos os propósitos específicos do usuário</li>
                  <li>Resultados específicos do uso do sistema</li>
                </ul>
                <p>
                  O Pudinzim Sorocaba não se responsabiliza por:
                </p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Perda de dados causada por falhas técnicas, desde que mantenhamos backup regular</li>
                  <li>Danos indiretos, incidentais ou consequenciais</li>
                  <li>Lucros cessantes ou perda de oportunidades de negócio</li>
                  <li>Decisões comerciais baseadas nos dados do sistema</li>
                </ul>
                <p>
                  Recomendamos fortemente que você mantenha backups próprios de informações críticas do seu negócio.
                </p>
              </div>
            </div>

            {/* 7. Modificações nos Termos */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">7.</span>
                Modificações nos Termos
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  O Pudinzim Sorocaba reserva-se o direito de modificar estes Termos de Serviço a qualquer momento. Quando fizermos alterações significativas, notificaremos você através do e-mail cadastrado ou por meio de um aviso na plataforma.
                </p>
                <p>
                  O uso continuado do serviço após a notificação de alterações constitui sua aceitação dos novos termos. Caso não concorde com as modificações, você pode cancelar sua conta a qualquer momento.
                </p>
              </div>
            </div>

            {/* 8. Lei Aplicável */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">8.</span>
                Lei Aplicável e Foro
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  Estes Termos de Serviço são regidos pelas leis da República Federativa do Brasil, especialmente:
                </p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018)</li>
                  <li>Marco Civil da Internet (Lei nº 12.965/2014)</li>
                  <li>Código de Defesa do Consumidor (Lei nº 8.078/1990)</li>
                  <li>Código Civil Brasileiro (Lei nº 10.406/2002)</li>
                </ul>
                <p>
                  Para dirimir quaisquer controvérsias decorrentes destes Termos, fica eleito o foro da comarca de Sorocaba/SP, com renúncia expressa a qualquer outro, por mais privilegiado que seja.
                </p>
              </div>
            </div>

            {/* 9. Contato */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <span className="text-primary font-bold">9.</span>
                Contato
              </h2>
              <div className="space-y-3 text-muted-foreground leading-relaxed">
                <p>
                  Para dúvidas, sugestões ou questões relacionadas a estes Termos de Serviço, entre em contato conosco:
                </p>
                <ul className="list-none space-y-2 pl-0">
                  <li><strong>E-mail:</strong> contato@pudinzimsorocaba.com.br</li>
                  <li><strong>Sistema:</strong> Através do menu "Configurações" → "Suporte"</li>
                </ul>
                <p>
                  Nossa equipe terá prazer em atendê-lo e responderá sua solicitação em até 48 horas úteis.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t py-6 mt-12">
        <div className="container max-w-4xl text-center text-sm text-muted-foreground">
          © 2025 Pudinzim Sorocaba. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
}
